import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Button from '../ui/Button';
import { useQuota } from '../../hooks/useQuota';
import {DISPLAY_QUOTA_THRESHOLD} from '../../constants/common.js'

const MIN_ROWS = 3;
const MAX_ROWS = 20;
const ROW_HEIGHT = 24;

const QueryBox = ({
	placeholder,
	submitText,
	actionLabel,
	onSubmit,
	showActionButton = true,
	onAction,
	size = 'medium',
}) => {
	const { t } = useTranslation();
	const [message, setMessage] = useState('');
	const [loading, setLoading] = useState(false);
	const textareaRef = useRef(null);
	const { quota, isBlocked } = useQuota();

	const sizeClasses = {
		medium: 'w-full max-w-2xl',
	};

	const handleSubmit = async () => {
		if (!message.trim()) return;
		setLoading(true);

		try {
			await onSubmit(message);
			setMessage('');
		} catch (error) {
			console.error('Error in QueryBox:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleAction = () => {
		if (onAction) {
			onAction();
		} else {
			setMessage('');
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSubmit();
		}
	};

	// Auto-resize textarea function
	const autoResizeTextarea = () => {
		if (textareaRef.current) {
			textareaRef.current.style.height = 'auto';
			const scrollHeight = textareaRef.current.scrollHeight;
			const newRows = Math.min(Math.ceil(scrollHeight / ROW_HEIGHT), MAX_ROWS);
			const rows = Math.max(newRows, MIN_ROWS);
			textareaRef.current.style.height = `${rows * ROW_HEIGHT}px`;
		}
	};

	// Resize on component mount and when message changes
	useEffect(() => {
		autoResizeTextarea();
	}, [message]);

	const focusInput = () => {
		textareaRef.current.focus();
	};

	return (
		<div
			className={`relative border border-border-default focus-within:ring-1 focus-within:ring-border-active rounded-sm p-4 gap-3 flex flex-col justify-between ${sizeClasses[size]}`}
			onClick={focusInput}
		>
			<textarea
				className='resize-none focus-visible:outline-none w-full'
				placeholder={placeholder || t('query.title', { app_name: t('general.app_name') })}
				value={message}
				onChange={(e) => setMessage(e.target.value)}
				onKeyDown={handleKeyDown}
				disabled={loading || isBlocked}
				ref={textareaRef}
				rows='3'
			/>
			<div className='flex justify-between items-end'>
				<div>
					{showActionButton && !loading && message.trim() !== '' && !isBlocked && (
						<Button size='tiny' type='line' onClick={handleAction}>
							{actionLabel || t('query.action_button')}
						</Button>
					)}
				</div>
				<div className='flex flex-row items-end gap-6'>
					<div className='mb-1'>
						{quota && (
							<span className='text-link text-sm font-medium'>
								{/* If the user lacks a tier, we only show the remaining quota from the teams shared quota. If the team does not share quota, then the remaining quota is always 0. */}
								{quota.total !== null && quota.total !== undefined
									? t('query.quota.remaining_quota', {
											remaining: quota.remaining,
											total: quota.total,
									  })
									: DISPLAY_QUOTA_THRESHOLD < quota.remaining
									? null
									: t('query.quota.remaining_quota_only', { remaining: quota.remaining})}
							</span>
						)}
					</div>

					<Button
						variant='primary'
						size='medium'
						type='solid'
						disabled={message.trim() === ''}
						onClick={handleSubmit}
						loading={loading}
					>
						{submitText || t('query.submit_button')}
					</Button>
				</div>
			</div>
		</div>
	);
};

QueryBox.propTypes = {
	placeholder: PropTypes.string,
	submitText: PropTypes.string,
	actionLabel: PropTypes.string,
	onSubmit: PropTypes.func.isRequired,
	showActionButton: PropTypes.bool,
	onAction: PropTypes.func,
	size: PropTypes.oneOf(['medium']),
};

export default QueryBox;
